import { LoadingState } from '@v2/design-system';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { CommissionGoal } from '@v2/pages/commissioning/types';
import { fmtBrl, fmtBrlShort, monthLabelCap } from './homeFormat';

type Props = {
    selectedMonth: string;
    goal: CommissionGoal | null;
    commissionGenerated: number;
    salesConsidered: number;
    isLoading?: boolean;
};

export default function SellerMonthSummaryCard({
    selectedMonth,
    goal,
    commissionGenerated,
    salesConsidered,
    isLoading,
}: Props) {
    const { t, locale } = useI18n();
    const dateLocale = locale === 'pt_BR' ? 'pt-BR' : locale === 'zh_CN' ? 'zh-CN' : locale === 'es' ? 'es' : 'en-US';
    const periodLabel = monthLabelCap(selectedMonth, dateLocale);
    const perf = goal?.performance;
    const pct = perf?.pct_global ?? 0;
    const barPct = Math.min(100, Math.max(4, pct * 100));
    const hit = pct >= 1;

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-800 dark:text-zinc-100">
                {t('v2.my_commission.my_month_title', { period: periodLabel })}
            </h2>

            {isLoading && <LoadingState variant="inline" className="py-8" />}

            {!isLoading && !goal && (
                <p className="py-6 text-center text-xs text-gray-400">{t('v2.my_commission.buckets_empty')}</p>
            )}

            {!isLoading && goal && (
                <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Stat
                            label={t('v2.my_commission.my_month_sales')}
                            value={fmtBrlShort(salesConsidered)}
                            hint={t('v2.my_commission.my_month_sales_hint')}
                        />
                        <Stat
                            label={t('v2.my_commission.my_month_goal')}
                            value={fmtBrlShort(goal.global_target)}
                        />
                        <Stat
                            label={t('v2.my_commission.my_month_pct')}
                            value={`${(pct * 100).toFixed(0)}%`}
                            valueClass={hit ? 'text-emerald-600' : 'text-orange-700'}
                        />
                        <Stat
                            label={t('v2.my_commission.my_month_commission')}
                            value={fmtBrl(commissionGenerated)}
                            valueClass="text-orange-700 dark:text-orange-300"
                        />
                    </div>

                    <div>
                        <div className="mb-1 flex justify-between text-[10px] font-semibold text-gray-500">
                            <span>0%</span>
                            <span className={hit ? 'text-emerald-600' : ''}>{(pct * 100).toFixed(0)}%</span>
                        </div>
                        <div className="h-3 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                            <div
                                className={cn('h-full rounded-full transition-all', hit ? 'bg-emerald-500' : 'bg-orange-500')}
                                style={{ width: `${barPct}%` }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function Stat({
    label,
    value,
    hint,
    valueClass,
}: {
    label: string;
    value: string;
    hint?: string;
    valueClass?: string;
}) {
    return (
        <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
            <p className={cn('text-lg font-black text-gray-900 dark:text-white', valueClass)}>{value}</p>
            {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
        </div>
    );
}
